// AuralizationEngine — the public, UI-facing orchestrator.
//
// Lifecycle:
//   const eng = new AuralizationEngine();
//   await eng.setStates({ before: untreated, after: treated });  // builds IRs
//   await eng.loadSourceUrl('/audio/samples/808.wav');           // or loadSourceFile(file)
//   await eng.prepare();                                         // convolve all states
//   eng.play('after'); eng.setActiveState('before'); ...         // transport
//
// The reference state (lowest panel count — the untreated baseline) is
// synthesized first; its modal scale is shared with every other state so the
// before/after LF-level difference is preserved (see irSynth.ts).

import type { RoomAcoustics } from './types';
import { synthesizeIR } from './irSynth';
import { convolveAll } from './convolve';

export interface EngineCallbacks {
  onStatus?: (status: string) => void;
  onReady?: (states: string[]) => void;
  onProgress?: (elapsed: number, duration: number) => void;
  onPlayingChange?: (isPlaying: boolean) => void;
  onEnded?: () => void;
}

export interface EngineOptions extends EngineCallbacks {
  irLengthSec?: number;
  modalTrimDb?: number;
}

/** A playback window into a source file, in seconds (both optional). Applied to
 *  the decoded buffer before convolution, so it trims cost + duration, not just
 *  playback. See SampleDef in samples.ts. */
export interface TrimWindow {
  startSec?: number;
  endSec?: number;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.AudioContext !== 'undefined';
}

/** Perf-timeline marks at the real pipeline boundaries, for the Playwright
 *  benchmark (web/tests/e2e/auralization-listen.bench.spec.ts) to read back
 *  via performance.getEntriesByType('mark'). No-ops outside a browser with the
 *  Performance API (SSR, older environments) — never throws, never observable
 *  to callers, so this can't affect the engine's public behavior. */
function mark(name: string): void {
  if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
    performance.mark(`auralization:${name}`);
  }
}

export class AuralizationEngine {
  private ctx: AudioContext | null = null;
  private readonly opts: EngineOptions;

  private irs: Record<string, AudioBuffer> = {};
  private convolved: Record<string, AudioBuffer> = {};
  private sourceBuffer: AudioBuffer | null = null;

  private activeState = '';
  private node: AudioBufferSourceNode | null = null;
  private gain: GainNode | null = null;
  private startedAt = 0;     // ctx time when current playback began
  private pauseOffset = 0;   // seconds into the buffer
  private playing = false;
  private rafId = 0;

  constructor(opts: EngineOptions = {}) {
    this.opts = opts;
  }

  /** Lazily create the AudioContext (browser only; safe to call repeatedly). */
  private getCtx(): AudioContext {
    if (!isBrowser()) throw new Error('AuralizationEngine requires a browser environment');
    if (!this.ctx) this.ctx = new AudioContext();
    return this.ctx;
  }

  get availableStates(): string[] {
    return Object.keys(this.irs);
  }
  get currentState(): string {
    return this.activeState;
  }
  get isPlaying(): boolean {
    return this.playing;
  }
  get duration(): number {
    return this.currentBuffer()?.duration ?? 0;
  }
  get currentTime(): number {
    if (this.playing && this.ctx) return this.pauseOffset + (this.ctx.currentTime - this.startedAt);
    return this.pauseOffset;
  }

  /**
   * Synthesize one IR per state. The state with the lowest panel count is the
   * reference; its modal scale is shared with the rest. Yields to the event
   * loop between states so a multi-state build doesn't freeze the UI.
   */
  async setStates(states: Record<string, RoomAcoustics>): Promise<void> {
    const ctx = this.getCtx();
    const ids = Object.keys(states);
    if (ids.length === 0) {
      this.irs = {};
      return;
    }
    this.opts.onStatus?.('Building room acoustics…');
    mark('setStates:start');

    // Reference = lowest nProducts (the untreated/baseline state).
    const refId = ids.reduce((a, b) => (states[a].nProducts <= states[b].nProducts ? a : b));
    const common = { irLengthSec: this.opts.irLengthSec, modalTrimDb: this.opts.modalTrimDb };

    const ref = synthesizeIR(ctx, states[refId], common);
    const irs: Record<string, AudioBuffer> = { [refId]: ref.buffer };

    for (const id of ids) {
      if (id === refId) continue;
      await Promise.resolve(); // yield a tick
      irs[id] = synthesizeIR(ctx, states[id], { ...common, modalScale: ref.modalScale }).buffer;
    }

    this.irs = irs;
    this.convolved = {}; // stale against new IRs
    if (!this.activeState || !irs[this.activeState]) this.activeState = refId;
    mark('setStates:end');

    if (this.sourceBuffer) {
      await this.prepare();
    } else {
      this.opts.onStatus?.('Room acoustics ready. Load audio to preview.');
    }
  }

  /** Create + resume the AudioContext. Call this synchronously from within a
   *  user gesture (a click) BEFORE any async source load, so the context is
   *  unlocked up front — browsers (notably iOS Safari) keep a context created
   *  outside a gesture suspended, which is silent playback. */
  resume(): void {
    if (!isBrowser()) return;
    const ctx = this.getCtx();
    if (ctx.state === 'suspended') void ctx.resume();
  }

  /** Load a dry source from a user-supplied File (upload path). */
  async loadSourceFile(file: File): Promise<void> {
    // Switching source: halt whatever is playing and reset the transport before
    // the new buffer loads, so the old clip can't keep running (or its progress
    // keep counting) under the new one.
    this.resume();
    this.stop();
    const buf = await file.arrayBuffer();
    await this.decodeAndStore(buf);
  }

  /** Load a dry source from a URL (bundled sample path). `trim` plays only a
   *  [startSec, endSec] window of the file (see SampleDef). */
  async loadSourceUrl(url: string, trim?: TrimWindow): Promise<void> {
    this.resume();
    this.stop();
    mark('fetch:start');
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch sample: ${res.status}`);
    const buf = await res.arrayBuffer();
    mark('fetch:end');
    await this.decodeAndStore(buf, trim);
  }

  private async decodeAndStore(data: ArrayBuffer, trim?: TrimWindow): Promise<void> {
    const ctx = this.getCtx();
    this.opts.onStatus?.('Decoding audio…');
    mark('decode:start');
    const decoded = await ctx.decodeAudioData(data);
    mark('decode:end');
    this.sourceBuffer = this.applyTrim(decoded, trim);
    if (Object.keys(this.irs).length > 0) await this.prepare();
  }

  /** Slice a decoded buffer to the [startSec, endSec] window. Clamps to the
   *  buffer bounds; an out-of-range or inverted window is ignored (returns the
   *  full buffer) so a bad config never produces silence. */
  private applyTrim(buf: AudioBuffer, trim?: TrimWindow): AudioBuffer {
    if (!trim || (trim.startSec == null && trim.endSec == null)) return buf;
    const sr = buf.sampleRate;
    const total = buf.length;
    const start = Math.max(0, Math.min(trim.startSec != null ? Math.floor(trim.startSec * sr) : 0, total));
    const end = Math.max(0, Math.min(trim.endSec != null ? Math.ceil(trim.endSec * sr) : total, total));
    if (end <= start || (start === 0 && end === total)) return buf;
    const ctx = this.getCtx();
    const out = ctx.createBuffer(buf.numberOfChannels, end - start, sr);
    for (let ch = 0; ch < buf.numberOfChannels; ch++) {
      out.copyToChannel(buf.getChannelData(ch).subarray(start, end), ch);
    }
    return out;
  }

  /** Convolve the loaded source against every state IR. */
  async prepare(): Promise<void> {
    if (!this.sourceBuffer || Object.keys(this.irs).length === 0) return;
    this.opts.onStatus?.('Processing convolution…');
    mark('prepare:start');
    this.convolved = await convolveAll(this.sourceBuffer, this.irs);
    mark('prepare:end');
    const ready = Object.keys(this.convolved);
    this.opts.onStatus?.(`Ready — ${ready.length} treatment state(s).`);
    mark('ready');
    this.opts.onReady?.(ready);
  }

  private currentBuffer(): AudioBuffer | null {
    return this.convolved[this.activeState] ?? this.sourceBuffer;
  }

  /** Start (or restart) playback of a state from the current offset. */
  play(stateId?: string): void {
    const ctx = this.getCtx();
    if (ctx.state === 'suspended') void ctx.resume();
    if (stateId) this.activeState = stateId;

    const buffer = this.currentBuffer();
    if (!buffer) return;
    this.stopNode();

    const node = ctx.createBufferSource();
    node.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = 1.0;
    node.connect(gain);
    gain.connect(ctx.destination);

    node.onended = () => {
      if (!this.playing) return; // manual stop already handled
      this.playing = false;
      this.pauseOffset = 0;
      this.stopRaf();
      this.opts.onPlayingChange?.(false);
      this.opts.onProgress?.(0, buffer.duration);
      this.opts.onEnded?.();
    };

    const offset = Math.min(this.pauseOffset, Math.max(0, buffer.duration - 0.05));
    this.node = node;
    this.gain = gain;
    this.startedAt = ctx.currentTime;
    this.playing = true;
    mark('play:start'); // node.start() below schedules playback essentially instantly.
    node.start(0, offset);
    this.opts.onPlayingChange?.(true);
    this.startRaf();
  }

  /** Pause, remembering the offset. */
  pause(): void {
    if (!this.playing || !this.ctx) return;
    this.pauseOffset += this.ctx.currentTime - this.startedAt;
    this.playing = false;
    this.stopNode();
    this.stopRaf();
    this.opts.onPlayingChange?.(false);
  }

  toggle(stateId?: string): void {
    if (this.playing) this.pause();
    else this.play(stateId);
  }

  /** Halt playback and rewind to the start. Unlike pause(), this discards the
   *  offset — used when the source is being swapped so the transport doesn't
   *  carry the previous clip's position (or keep counting) into the new one. */
  stop(): void {
    const wasPlaying = this.playing;
    this.playing = false;
    this.stopNode();
    this.stopRaf();
    this.pauseOffset = 0;
    if (wasPlaying) this.opts.onPlayingChange?.(false);
    this.opts.onProgress?.(0, 0);
  }

  /** Switch the active before/after state, seamlessly if currently playing. */
  setActiveState(stateId: string): void {
    if (stateId === this.activeState) return;
    const wasPlaying = this.playing;
    if (wasPlaying) this.pause();
    this.activeState = stateId;
    if (wasPlaying) this.play();
  }

  /** Seek to a time (seconds). */
  seek(seconds: number): void {
    const buffer = this.currentBuffer();
    if (!buffer) return;
    const wasPlaying = this.playing;
    if (wasPlaying) this.pause();
    this.pauseOffset = Math.max(0, Math.min(seconds, buffer.duration));
    if (wasPlaying) this.play();
    else this.opts.onProgress?.(this.pauseOffset, buffer.duration);
  }

  private stopNode(): void {
    if (this.node) {
      try { this.node.onended = null; this.node.stop(); } catch { /* already stopped */ }
      try { this.node.disconnect(); } catch { /* noop */ }
      this.node = null;
    }
    if (this.gain) {
      try { this.gain.disconnect(); } catch { /* noop */ }
      this.gain = null;
    }
  }

  private startRaf(): void {
    if (!isBrowser()) return;
    const tick = () => {
      if (!this.playing) return;
      const buffer = this.currentBuffer();
      if (buffer) this.opts.onProgress?.(this.currentTime, buffer.duration);
      this.rafId = window.requestAnimationFrame(tick);
    };
    this.rafId = window.requestAnimationFrame(tick);
  }

  private stopRaf(): void {
    if (this.rafId && isBrowser()) window.cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  /** Release audio resources. */
  dispose(): void {
    this.stopNode();
    this.stopRaf();
    this.playing = false;
    if (this.ctx) {
      void this.ctx.close();
      this.ctx = null;
    }
    this.irs = {};
    this.convolved = {};
    this.sourceBuffer = null;
  }
}
